import { observer, useLocalObservable } from 'mobx-react';
import { Fragment } from 'react';
import {
  getErrorFields,
  PORTFOLIO_SEARCH_CATEGORY_FIELDS,
  SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA,
  trimStateFields
} from '../../../../utils/constants/fields';
import { mapFieldsToState } from '../../../../utils/utils';
import { paths } from '../../../../utils/constants/routes';
import { UI_OPTIONS } from '../../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../../utils/constants/uiOptionKeys';
import useHistory from '../../../../hooks/useHistory';
import { getPortfoliosInvestmentCriteriaSearchFields } from '../agent/constants/portfoliosInvestmentCriteriaSearchFields';
import PortfoliosInvestmentCriteriaSearchFormDesktop from '../agent/PortfoliosInvestmentCriteriaSearchFormDesktop';
import PortfoliosInvestmentCriteriaSearchFormMobile from '../agent/PortfoliosInvestmentCriteriaSearchFormMobile';

/**
 * PortfolioSearch - Self-contained Portfolio Companies search component
 *
 * Manages its own state for:
 * - Investment criteria fields
 * - Validation and search execution
 *
 * @param {React.ReactNode} tabs - Tab navigation component from parent
 * @param {Object} searchTypeInput - Search type input component for mobile
 */
const PortfolioSearch = observer(({ tabs, searchTypeInput }) => {
  const { navigate } = useHistory();

  const state = useLocalObservable(() => ({
    // Category state
    categoryFields: {
      [PORTFOLIO_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME]: [
        UI_OPTIONS[UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY]['Investment criteria']
      ]
    },
    setCategoryFieldValue: (field = {}, value) => {
      state.categoryFields[field.NAME] = value;
    },
    get categories() {
      return Object.entries(UI_OPTIONS[UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY]).map(
        ([name, value]) => ({
          name,
          value,
          disabled:
            value !== UI_OPTIONS[UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY]['Investment criteria']
        })
      );
    },

    // Fields state
    fields: {
      ...mapFieldsToState(SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA),
      [SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA.SEARCH_TYPE.NAME]: [
        UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.']
      ]
    },
    setFieldValue: (field = {}, value) => {
      state.fields[field.NAME] = value;
    },

    // Validation state
    onSubmitErrorState: false,
    setOnSubmitErrorState: (value = false) => (state.onSubmitErrorState = value),
    get validationFields() {
      return getErrorFields(
        Object.values(SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA),
        state.fields
      );
    },
    get isSearchDisabled() {
      return (
        !!state.validationFields.invalidFields.filter((f) => !f.isOnSubmit).length ||
        (state.onSubmitErrorState && !!state.validationFields.invalidFields.length)
      );
    },

    // Compatibility layer for field builders that expect old state shape
    get portfoliosInvestmentCriteriaFields() {
      return state.fields;
    },
    get setPortfoliosInvestmentCriteriaFieldValue() {
      return state.setFieldValue;
    }
  }));

  const executeSearch = () => {
    const selectedCategory =
      state.categoryFields[PORTFOLIO_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME][0];

    if (
      selectedCategory !== UI_OPTIONS[UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY]['Investment criteria']
    ) {
      return;
    }

    trimStateFields(state.fields);
    if (state.validationFields.invalidFields.length) {
      if (!state.onSubmitErrorState) {
        state.setOnSubmitErrorState(true);
      }
      return;
    }

    const fields = Object.entries(state.fields)
      .map(([k, v]) => `${[k]}=${encodeURIComponent(JSON.stringify(v))}`)
      .join('&');

    navigate(
      paths.SEARCH_RESULTS +
        `?${PORTFOLIO_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME}=${encodeURIComponent(
          selectedCategory
        )}&page=1&${fields}`
    );
  };

  const onSearch = (e) => {
    e?.preventDefault?.();
    executeSearch();
  };

  // Build field components
  const fieldComponents = getPortfoliosInvestmentCriteriaSearchFields(state);

  // Only show Investment criteria for now (other categories disabled)
  const selectedCategory =
    state.categoryFields[PORTFOLIO_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME][0];
  if (
    selectedCategory !== UI_OPTIONS[UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY]['Investment criteria']
  ) {
    return null;
  }

  return (
    <Fragment>
      <PortfoliosInvestmentCriteriaSearchFormDesktop
        fields={fieldComponents}
        isSearchDisabled={state.isSearchDisabled}
        onSearch={onSearch}
        tabs={tabs}
        categoryFields={state.categoryFields}
        setCategoryFieldValue={state.setCategoryFieldValue}
        categoryOptions={state.categories}
      />
      <PortfoliosInvestmentCriteriaSearchFormMobile
        searchTypeInput={searchTypeInput}
        isSearchDisabled={state.isSearchDisabled}
        onSearch={onSearch}
        fields={fieldComponents}
        categoryFields={state.categoryFields}
        setCategoryFieldValue={state.setCategoryFieldValue}
        categoryOptions={state.categories.filter(({ disabled }) => !disabled)}
      />
    </Fragment>
  );
});

export default PortfolioSearch;
